'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; /**
                                                                                                                                                                                                                                                                               * Copyright 2013-2016, Hudl
                                                                                                                                                                                                                                                                               * All rights reserved.
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               * This source code is licensed under the BSD-style license found in the
                                                                                                                                                                                                                                                                               * LICENSE file in the root directory of this source tree. An additional grant
                                                                                                                                                                                                                                                                               * of patent rights can be found in the PATENTS file in the same directory.
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               * Updates the state based on the object and informs the VideoWrapper of state changes
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               * @providesModule UpdateState
                                                                                                                                                                                                                                                                               * @private
                                                                                                                                                                                                                                                                               *
                                                                                                                                                                                                                                                                               * @param {Object} updateObject State changes that need to be processed
                                                                                                                                                                                                                                                                               * @param {Array<string>} videoFunc changes that should be applied to the video
                                                                                                                                                                                                                                                                               * @param {boolean} force Force the updates, skips the intersection logic
                                                                                                                                                                                                                                                                               */

/* eslint-disable no-console */

exports.default = updateState;

var _Listener = require('./Listener');

var _State = require('./State');

var _State2 = _interopRequireDefault(_State);

var _immutable = require('immutable');

var _store = require('../../store');

var _store2 = _interopRequireDefault(_store);

var _setPlayables = require('../../store/actions/players/setPlayables');

var _setPlayables2 = _interopRequireDefault(_setPlayables);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var reduxKeys = [];

/**
 * For each key in the updateObject check if it exists on the redux state,
 * if it does we know that it has been migrated, yet a component is still utilising
 * the updateState legacy cycle to update it, so we should intercept it and dispatch a
 * custom MIGRATION action which will forcefully replace the state on that new redux key
 * with the provided value.
 *
 * @param {Object} updateObject - The full updateObject
 * @param {String} key - key to check against on the updateObject
 * @returns {boolean} - whether or not the provided key has been migrated
 */
var reduxSync = function reduxSync(updateObject, key) {
  if (reduxKeys.indexOf(key) !== -1) {
    var type = key.toUpperCase() + '_MIGRATION';
    _store2.default.storeInstance().dispatch({
      type: type,
      value: updateObject[key]
    });
    return true;
  }
  return false;
};

/*
 * This is a map for specific logic when updating the video
 */
var videoUpdateMaps = {
  // must set the value to seconds for updating the video
  currentTimeMs: function currentTimeMs(value) {
    return value / 1000;
  }
};
var playablesKey = 'playables';
var exclude = ['duration', 'durationMs', '_internal', 'viNode', '_state'];

var cacheSnapshotKeys = ['currentTime', 'currentTimeMs', 'focused', 'queueEnded'];

var nonBulkKeys = ['currentTime', 'currentTimeMs', 'src'];

var optimisticKeys = [].concat(nonBulkKeys, ['queueEnded', 'contextMenuOpen', 'isSeeking', 'focused', 'volume', 'muted',
// TODO: Remove tagging from this when perf is stable
'tagging']);

// We don't want this key as optimistic
nonBulkKeys.push('playingIndex');

var videoPropertyMap = {
  pause: 'paused',
  play: 'paused',
  currentTimeMs: 'currentTime'
};

function getterResolver(key, immutableObject) {
  var result = undefined;
  switch (key) {
    case 'currentTimeMs':
      result = immutableObject.currentTimeMs;
      break;
    case 'currentTime':
      result = immutableObject.currentTime;
      break;
    case 'playingIndex':
      result = immutableObject.playingIndex;
      break;
    default:
      return immutableObject.getImmutable(key);
  }
  return result;
}

var checkOptimistic = function checkOptimistic(k) {
  return optimisticKeys.indexOf(k) !== -1;
};

function updateState(updateObject) {
  var videoAccessors = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var force = arguments[2];

  var dirty = {};
  var bulk = {};
  var newValue = void 0;
  var oldValue = void 0;
  var emitChanges = false;
  var cachedSnapshot = false;
  var keys = Object.keys(updateObject);
  if (_store2.default.storeInstance().getState) {
    reduxKeys = Object.keys(_store2.default.storeInstance().getState()) || [];
  }

  if (keys.length === 1 && optimisticKeys.indexOf(keys[0]) !== -1 || keys.every(checkOptimistic)) {
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = keys[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var key = _step.value;

        if (!reduxSync(updateObject, key)) {
          if (cacheSnapshotKeys.indexOf(key) > -1) {
            cachedSnapshot = true;
          }
          _State2.default.setState(key, updateObject[key]);
          dirty[key] = updateObject[key];
        }
        emitChanges = true;
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }
  } else {
    var _iteratorNormalCompletion2 = true;
    var _didIteratorError2 = false;
    var _iteratorError2 = undefined;

    try {
      for (var _iterator2 = keys[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
        var _key = _step2.value;

        if (!updateObject.hasOwnProperty(_key) || exclude.indexOf(_key) !== -1) {
          continue;
        }
        if (_key === playablesKey) {
          var stateKeys = Object.keys(_store2.default.storeInstance().getState().players);
          if (stateKeys.length) {
            _store2.default.storeInstance().dispatch((0, _setPlayables2.default)(stateKeys[0], updateObject[playablesKey], false));
          }
        }
        newValue = updateObject[_key];
        if (!newValue.toJS && ((typeof newValue === 'undefined' ? 'undefined' : _typeof(newValue)) === 'object' || Array.isArray(newValue))) {
          newValue = (0, _immutable.fromJS)(updateObject[_key]);
        } else {
          newValue = updateObject[_key];
        }

        if (newValue === undefined) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Invalid attempt to update ', _key, ' with undefined');
          }
          continue;
        }

        oldValue = getterResolver(_key, _State2.default.state);
        if (oldValue === undefined && reduxSync(updateObject, _key)) {
          emitChanges = true;
          continue;
        } else if (oldValue === undefined) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Invalid update key:', _key, ' - not defined on state');
          }
          continue;
        }
        var isUpdated = false;
        if (newValue !== null && newValue.equals && oldValue !== null && oldValue.equals) {
          isUpdated = !newValue.equals(oldValue);
        } else {
          isUpdated = newValue !== oldValue;
        }
        if (isUpdated || force) {
          emitChanges = true;
          if (!reduxSync(updateObject, _key)) {
            dirty[_key] = newValue;
            var computed = nonBulkKeys.indexOf(_key) > -1;
            if (!computed) {
              bulk[_key] = newValue;
            }
            if (keys.length === 1 || computed) {
              _State2.default.setState(_key, newValue);
            }
          }
        }
      }
    } catch (err) {
      _didIteratorError2 = true;
      _iteratorError2 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion2 && _iterator2.return) {
          _iterator2.return();
        }
      } finally {
        if (_didIteratorError2) {
          throw _iteratorError2;
        }
      }
    }

    if (keys.length > 1 && Object.keys(bulk).length > 0) {
      _State2.default.setBulkState(bulk);
    }
  }
  if (!Array.isArray(videoAccessors)) return;
  var playerId = Object.keys(_store2.default.storeInstance().getState().players)[0];
  var player = _store2.default.getPlayerInstance(playerId);
  var _iteratorNormalCompletion3 = true;
  var _didIteratorError3 = false;
  var _iteratorError3 = undefined;

  try {
    for (var _iterator3 = videoAccessors[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
      var accessor = _step3.value;

      var updateObjKey = accessor;
      if (accessor === 'currentTime') {
        updateObjKey = 'currentTimeMs';
      }

      if (force || dirty[accessor] !== undefined || dirty[videoPropertyMap[accessor]] !== undefined || videoUpdateMaps[updateObjKey] !== undefined) {
        var updateValue = updateObject[updateObjKey];

        if (videoUpdateMaps[updateObjKey]) {
          updateValue = videoUpdateMaps[updateObjKey](updateValue);
        }

        if (typeof player[accessor] === 'function') {
          player[accessor](updateValue);
        } else {
          player[accessor] = updateValue;
        }
      }
    }

    /*
     * only emit listener if an actual change was made to the store
     * This is a perf optimization to only do react diffs when an intentional
     * change was made
     */
  } catch (err) {
    _didIteratorError3 = true;
    _iteratorError3 = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion3 && _iterator3.return) {
        _iterator3.return();
      }
    } finally {
      if (_didIteratorError3) {
        throw _iteratorError3;
      }
    }
  }

  if (emitChanges) {
    (0, _Listener.listener)(cachedSnapshot ? _State2.default.getCachedSnapshot() : _State2.default.generateSnapshot());
  }
}
module.exports = exports['default'];